import { useState, useMemo } from 'react';
import {
  Download,
  Wallet,
  Loader2,
  Printer,
  Search,
  TrendingUp,
  TrendingDown,
  Users,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { format } from 'date-fns';
import { DateRangeSelect, type DateRangeValue } from '@/components/ui/date-range-select';
import { datePresets, presetValue } from '@/lib/datePresets';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

type TransactionType = 'all' | 'top_up' | 'payment' | 'refund' | 'adjustment';
type AccountStatus = 'all' | 'active' | 'suspended' | 'inactive';

const CHART_COLORS = [
  'hsl(142, 76%, 36%)',  // green
  'hsl(217, 91%, 60%)',  // blue
  'hsl(25, 95%, 53%)',   // orange
  'hsl(263, 70%, 50%)',  // purple
];

const DATE_RANGE_PRESETS = datePresets('all_time', 'week', 'month', 'last_month', 'year');

const TRANSACTION_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'top_up', label: 'Top Up' },
  { value: 'payment', label: 'Payment' },
  { value: 'refund', label: 'Refund' },
  { value: 'adjustment', label: 'Adjustment' },
];

export default function StoreCreditReports() {
  const { organization } = useAuth();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'Store credit report' });

  const [transactionType, setTransactionType] = useState<TransactionType>('all');
  const [dateSel, setDateSel] = useState<DateRangeValue>(() => presetValue('month'));
  const [accountStatus, setAccountStatus] = useState<AccountStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate date filters ('all_time' means no date filter)
  const dateFilters = useMemo(
    () =>
      dateSel.preset === 'all_time'
        ? null
        : { start: dateSel.start.toISOString(), end: dateSel.end.toISOString() },
    [dateSel]
  );

  // Fetch customer accounts with wallet balances
  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['store-credit-accounts', organization?.id, accountStatus],
    queryFn: async () => {
      if (!organization?.id) return [];

      let query = supabase
        .from('customer_accounts')
        .select(`
          id,
          username,
          customer_number,
          wallet_balance,
          status,
          last_login_at,
          created_at,
          customer:customers(id, first_name, last_name, email, phone)
        `)
        .eq('organization_id', organization.id)
        .order('wallet_balance', { ascending: false });

      if (accountStatus !== 'all') {
        query = query.eq('status', accountStatus);
      }

      const { data, error } = await query.limit(500);

      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch wallet transactions
  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ['store-credit-transactions', organization?.id, transactionType, dateFilters],
    queryFn: async () => {
      if (!organization?.id) return [];

      let query = supabase
        .from('wallet_transactions')
        .select(`
          id,
          transaction_type,
          amount,
          balance_before,
          balance_after,
          notes,
          created_at,
          customer_account:customer_accounts(
            id,
            username,
            customer_number,
            customer:customers(first_name, last_name, email)
          ),
          processed_by:profiles(first_name, last_name)
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false });

      if (transactionType !== 'all') {
        query = query.eq('transaction_type', transactionType);
      }

      if (dateFilters) {
        query = query.gte('created_at', dateFilters.start).lte('created_at', dateFilters.end);
      }

      const { data, error } = await query.limit(500);

      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  const isLoading = accountsLoading || transactionsLoading;

  // Filter accounts by search
  const filteredAccounts = useMemo(() => {
    if (!accounts) return [];
    if (!searchQuery) return accounts;

    const query = searchQuery.toLowerCase();
    return accounts.filter(acc => {
      const customer = acc.customer as any;
      return (
        acc.username?.toLowerCase().includes(query) ||
        acc.customer_number?.toLowerCase().includes(query) ||
        customer?.email?.toLowerCase().includes(query) ||
        `${customer?.first_name || ''} ${customer?.last_name || ''}`.toLowerCase().includes(query)
      );
    });
  }, [accounts, searchQuery]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!accounts?.length || !transactions) return null;

    const totalBalance = accounts.reduce((sum, acc) => sum + (acc.wallet_balance || 0), 0);
    const activeAccounts = accounts.filter(acc => acc.status === 'active').length;
    const accountsWithBalance = accounts.filter(acc => (acc.wallet_balance || 0) > 0).length;

    // Transaction stats
    const topUps = transactions.filter(t => t.transaction_type === 'top_up');
    const payments = transactions.filter(t => t.transaction_type === 'payment');
    const refunds = transactions.filter(t => t.transaction_type === 'refund');

    const totalTopUps = topUps.reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const totalPayments = payments.reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const totalRefunds = refunds.reduce((sum, t) => sum + Math.abs(t.amount), 0);

    return {
      totalAccounts: accounts.length,
      activeAccounts,
      accountsWithBalance,
      totalBalance,
      avgBalance: accounts.length > 0 ? totalBalance / accounts.length : 0,
      totalTopUps,
      totalPayments,
      totalRefunds,
      netFlow: totalTopUps - totalPayments + totalRefunds,
    };
  }, [accounts, transactions]);

  // Chart data - daily transaction flow
  const dailyFlowData = useMemo(() => {
    if (!transactions?.length) return [];

    const dailyMap = new Map<string, { topUp: number; payment: number; refund: number }>();
    
    transactions.forEach(txn => {
      const date = format(new Date(txn.created_at), 'MMM d');
      if (!dailyMap.has(date)) {
        dailyMap.set(date, { topUp: 0, payment: 0, refund: 0 });
      }
      const day = dailyMap.get(date)!;
      if (txn.transaction_type === 'top_up') day.topUp += Math.abs(txn.amount);
      if (txn.transaction_type === 'payment') day.payment += Math.abs(txn.amount);
      if (txn.transaction_type === 'refund') day.refund += Math.abs(txn.amount);
    });

    return Array.from(dailyMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .reverse()
      .slice(-14);
  }, [transactions]);

  // Chart data - balance distribution
  const balanceDistribution = useMemo(() => {
    if (!accounts?.length) return [];

    const ranges = [
      { name: '$0', min: 0, max: 0.01 },
      { name: '$1-50', min: 0.01, max: 50 },
      { name: '$50-100', min: 50, max: 100 },
      { name: '$100-250', min: 100, max: 250 },
      { name: '$250-500', min: 250, max: 500 },
      { name: '$500+', min: 500, max: Infinity },
    ];

    return ranges.map(range => ({
      name: range.name,
      count: accounts.filter(acc => {
        const bal = acc.wallet_balance || 0;
        return bal >= range.min && bal < range.max;
      }).length,
    }));
  }, [accounts]);

  const chartConfig: ChartConfig = {
    topUp: { label: 'Top Up', color: CHART_COLORS[0] },
    payment: { label: 'Payment', color: CHART_COLORS[1] },
    refund: { label: 'Refund', color: CHART_COLORS[2] },
    count: { label: 'Accounts', color: CHART_COLORS[0] },
  };

  // Export CSV
  const exportToCSV = () => {
    if (!filteredAccounts.length) return;

    const headers = ['Username', 'Customer Number', 'Customer Name', 'Email', 'Balance', 'Status', 'Last Login', 'Created'];
    const rows = filteredAccounts.map(acc => {
      const customer = acc.customer as any;
      return [
        acc.username,
        acc.customer_number,
        customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : '-',
        customer?.email || '-',
        acc.wallet_balance,
        acc.status,
        acc.last_login_at ? format(new Date(acc.last_login_at), 'yyyy-MM-dd HH:mm') : 'Never',
        format(new Date(acc.created_at), 'yyyy-MM-dd'),
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `store-credit-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>;
      case 'suspended':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Suspended</Badge>;
      case 'inactive':
        return <Badge variant="destructive">Inactive</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTransactionBadge = (type: string) => {
    switch (type) {
      case 'top_up':
        return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          <ArrowDownRight className="h-3 w-3 mr-1" />Top Up
        </Badge>;
      case 'payment':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          <ArrowUpRight className="h-3 w-3 mr-1" />Payment
        </Badge>;
      case 'refund':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
          Refund
        </Badge>;
      case 'adjustment':
        return <Badge variant="secondary">Adjustment</Badge>;
      default:
        return <Badge variant="secondary">{type}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Store Credit Reports</h1>
          <p className="text-sm text-muted-foreground">
            Monitor store credit issuance, usage, and customer balances
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Accounts</span>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summaryStats?.totalAccounts || 0}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {summaryStats?.activeAccounts || 0} active, {summaryStats?.accountsWithBalance || 0} with balance
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Balance</span>
              <Wallet className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalBalance || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Avg: {formatCurrency(summaryStats?.avgBalance || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Top-ups</span>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2 text-green-600">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalTopUps || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Payments</span>
              <TrendingDown className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2 text-blue-600">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalPayments || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or account..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={accountStatus} onValueChange={(v) => setAccountStatus(v as AccountStatus)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={transactionType} onValueChange={(v) => setTransactionType(v as TransactionType)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRANSACTION_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DateRangeSelect presets={DATE_RANGE_PRESETS} value={dateSel} onChange={setDateSel} />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Transaction Flow */}
            <Card>
              <CardHeader>
                <CardTitle>Transaction Flow</CardTitle>
                <CardDescription>Daily top-ups, payments, and refunds</CardDescription>
              </CardHeader>
              <CardContent>
                {dailyFlowData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-[250px]">
                    <BarChart data={dailyFlowData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `$${value}`}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            valueFormatter={(value) => formatCurrency(value)}
                          />
                        }
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                      <Bar dataKey="topUp" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="payment" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="refund" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No transaction data
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Balance Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Balance Distribution</CardTitle>
                <CardDescription>Accounts by balance range</CardDescription>
              </CardHeader>
              <CardContent>
                {balanceDistribution.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-[250px]">
                    <BarChart data={balanceDistribution}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="name"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            valueFormatter={(value) => `${value} accounts`}
                          />
                        }
                      />
                      <Bar dataKey="count" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No account data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Customer Accounts Table */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Accounts</CardTitle>
              <CardDescription>
                {filteredAccounts.length} accounts with store credit
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Customer</TableHead>
                    <TableHead>Account #</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.slice(0, 50).map((account) => {
                    const customer = account.customer as any;

                    return (
                      <TableRow key={account.id}>
                        <TableCell>
                          <div>
                            <span className="font-medium">
                              {customer 
                                ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || account.username
                                : account.username
                              }
                            </span>
                            {customer?.email && (
                              <p className="text-xs text-muted-foreground">{customer.email}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {account.customer_number}
                        </TableCell>
                        <TableCell>{getStatusBadge(account.status)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {formatCurrency(account.wallet_balance)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {account.last_login_at 
                            ? format(new Date(account.last_login_at), 'MMM d, yyyy')
                            : 'Never'
                          }
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(account.created_at), 'MMM d, yyyy')}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredAccounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No accounts found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {filteredAccounts.length > 50 && (
                <p className="text-sm text-muted-foreground mt-4 text-center">
                  Showing first 50 of {filteredAccounts.length} accounts
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          {transactions && transactions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>Latest wallet activity</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Balance After</TableHead>
                      <TableHead>Processed By</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.slice(0, 50).map((txn) => {
                      const account = txn.customer_account as any;
                      const customer = account?.customer;
                      const processedBy = txn.processed_by as any;

                      return (
                        <TableRow key={txn.id}>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {format(new Date(txn.created_at), 'MMM d, h:mm a')}
                          </TableCell>
                          <TableCell>
                            <div>
                              <span className="font-medium">
                                {customer 
                                  ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                                  : account?.username || 'Unknown'
                                }
                              </span>
                              <p className="text-xs text-muted-foreground">{account?.customer_number}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getTransactionBadge(txn.transaction_type)}</TableCell>
                          <TableCell className={`text-right tabular-nums font-medium ${
                            txn.transaction_type === 'top_up' || txn.transaction_type === 'refund'
                              ? 'text-green-600'
                              : 'text-blue-600'
                          }`}>
                            {txn.transaction_type === 'top_up' || txn.transaction_type === 'refund' ? '+' : '-'}
                            {formatCurrency(Math.abs(txn.amount))}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(txn.balance_after)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {processedBy 
                              ? `${processedBy.first_name || ''} ${processedBy.last_name || ''}`.trim()
                              : 'System'
                            }
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate text-muted-foreground">
                            {txn.notes || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Print template */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Store Credit Report</h1>
            <div className="print-meta">
              Period: {DATE_RANGE_PRESETS.find(p => p.key === dateSel.preset)?.label
                ?? `${format(dateSel.start, 'MMM d, yyyy')} – ${format(dateSel.end, 'MMM d, yyyy')}`}
            </div>
          </div>
          <div className="print-meta">
            Generated on {new Date().toLocaleString()}
          </div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Total Accounts</div>
            <div className="value">{summaryStats?.totalAccounts || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Balance</div>
            <div className="value">{formatCurrency(summaryStats?.totalBalance || 0)}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Top-ups</div>
            <div className="value">{formatCurrency(summaryStats?.totalTopUps || 0)}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Payments</div>
            <div className="value">{formatCurrency(summaryStats?.totalPayments || 0)}</div>
          </div>
        </div>

        <div className="print-section">
          <h2>Customer Accounts</h2>
          {filteredAccounts.length > 0 ? (
            <table className="print-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Account #</th>
                  <th>Email</th>
                  <th>Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((acc) => {
                  const customer = acc.customer as any;
                  return (
                    <tr key={acc.id}>
                      <td>{customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : acc.username}</td>
                      <td>{acc.customer_number}</td>
                      <td>{customer?.email || '-'}</td>
                      <td>{formatCurrency(acc.wallet_balance)}</td>
                      <td>{acc.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="print-meta">No accounts found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
